import styled from 'styled-components'
import 'react-base-table/styles.css'
import { Resizable } from 'react-resizable'
import 'react-resizable/css/styles.css'
import React, { useEffect, useRef, useState, useLayoutEffect } from 'react'
import { Checkbox, Input, Select, Tag, Table, DatePicker, Tooltip, message, Space } from 'antd'
import {
  AgeBasedDiscountQuestion,
  DirectUploadButton,
  GeographyBasedDiscountQuestion,
  InputDate,
  InputDateRange,
  InputDateTime,
  InputTime,
} from '@/components'
import { FileOutlined, UploadOutlined } from '@ant-design/icons'
import CONSTANTS from '@/utils/constants'
import moment from 'moment'
import { useWindowSize } from '@/hooks'
import { Auth, Storage } from 'aws-amplify'; 
import { s3Upload } from '@/utils/aws'

const CellContainer = styled.div`
  display: flex;
  flex: 1 0 100%;
  align-items: center;
  height: 100%;
  overflow: hidden;
  margin: 0 -5px;
  padding: 5px;
  border: 1px dashed transparent;
`

export const EditableCell = ({
  cellData,
  column,
  onChange = () => {},
  onDataChange,
  rowData,
  permission,
}) => {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(cellData)
  const inputRef = useRef()
  const handleClick = () => {
    if (permission == 'edit') setEditing(true)
  }
  const handleHide = () => {
    setEditing(false)
    onChange(value, column, rowData)
  }
  const onFileUpload = async (file) => {
    if (file.length > 0) {
      onChange(file[0], column, rowData)
      setValue(file[0])
      setEditing(false)
    }
  }
  useEffect(() => {
    if (cellData != value) {
      setValue(cellData)
    }
  }, [cellData])
  useEffect(() => {
    if (inputRef?.current?.focus) inputRef?.current?.focus()
  }, [editing])

  const renderInput = () => {
    switch (column?.type) {
      case CONSTANTS.FIELD_TYPES.MULTI_LINE:
        return (
          <Input.TextArea
            ref={inputRef}
            onPressEnter={handleHide}
            onBlur={handleHide}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        )
      case CONSTANTS.FIELD_TYPES.ONE_CHOICE:
        return (
          <Select
            style={{ width: '100%' }}
            ref={inputRef}
            value={value}
            onChange={(val) => setValue(val)}
            onBlur={handleHide}
            dropdownMatchSelectWidth={false} 
            dropdownStyle={{ minWidth: 250 }}
          >
            {column?.options?.map((v) => {
              return (
                <Select.Option key={v} value={v.optionText}>
                  {v.optionText}
                </Select.Option>
              )
            })}
          </Select>
        )
      case CONSTANTS.FIELD_TYPES.MULTI_CHOICE:
        return (
          <Select
            mode="multiple"
            ref={inputRef}
            value={value || []}
            onChange={(val) => setValue(val)}
            onBlur={handleHide}
            style={{ width: '100%' }}
          >
            {column?.options?.map((v, i) => {
              return (
                <Select.Option key={i} value={v.optionText}>
                  {v.optionText}
                </Select.Option>
              )
            })}
          </Select>
        )
      case CONSTANTS.FIELD_TYPES.YES_NO:
        return (
          <div style={{ width: '100%' }}>
            <Checkbox
              ref={inputRef}
              checked={value}
              onChange={(e) => setValue(e.target.checked)}
              onBlur={handleHide}
            />
          </div>
        )
      case CONSTANTS.FIELD_TYPES.DATE:
        return (
          <div style={{ width: '100%' }}>
            <InputDate
              ref={inputRef}
              value={value}
              onChange={(v) => setValue(v)}
              onBlur={handleHide}
            />
          </div>
        )
      case CONSTANTS.FIELD_TYPES.DATE_TIME:
        return (
          <div style={{ width: '100%' }}>
            <InputDateTime
              showTime={true}
              ref={inputRef}
              value={value}
              onChange={(v) => setValue(v)}
              onBlur={handleHide}
            />
          </div>
        )
      case CONSTANTS.FIELD_TYPES.DATE_RANGE:
        return (
          <div style={{ width: '100%' }}>
            <InputDateRange
              ref={inputRef}
              value={value}
              onChange={(v) => setValue(v)}
              onBlur={handleHide}
            />
          </div>
        )
      case CONSTANTS.FIELD_TYPES.AGE_BASED_DISCOUNT:
        return (
          <div style={{ width: '100%' }}>
            <AgeBasedDiscountQuestion
              ref={inputRef}
              value={value}
              onChange={(v) => setValue(v)}
              onBlur={handleHide}
            />
          </div>
        )
      case CONSTANTS.FIELD_TYPES.GEOGRAPHY_BASED_DISCOUNT:
        return (
          <div style={{ width: '100%' }}>
            <GeographyBasedDiscountQuestion
              ref={inputRef}
              value={value}
              onChange={(v) => setValue(v)}
              onBlur={handleHide}
            />
          </div>
        )
      case CONSTANTS.FIELD_TYPES.TIME:
        return (
          <div style={{ width: '100%' }}>
            <InputTime
              ref={inputRef}
              value={value}
              onChange={(v) => setValue(v)}
              onBlur={handleHide}
            />
          </div>
        )
      default:
        return (
          <Input
            value={value}
            ref={inputRef}
            onChange={(e) => {
              setValue(e.target.value)
            }}
            onPressEnter={handleHide}
            onBlur={handleHide}
          />
        )
    }
  }
  const renderValue = () => {
    switch (column?.type) {
      case CONSTANTS.FIELD_TYPES.FILE_UPLOAD:
        console.log('FILE UPLOAD VALUE via renderValue:', value);
        if (value && typeof value === 'string') {
          let filename = value.split('/').pop()
          const separatorIndex = filename.indexOf('-');
          if (separatorIndex !== -1) {
              filename = filename.substring(separatorIndex + 1); // Becomes "receipt.jpg"
          }
          return (
            <a href={value} target="_blank">
              <FileOutlined title={filename} />
            </a>
          )
        }
        return value
      case CONSTANTS.FIELD_TYPES.MULTI_CHOICE:
        if (typeof value !== 'string') {
          const totalTags = value?.length || 0
          const tags = value?.map((o) => (
            <Tag key={o} title={o}>
              {o}
            </Tag>
          ))
          return (
            <Tooltip title={tags} color="white">
              {value?.slice(0, 2)?.map((o) => (
                <Tag key={o} title={o}>
                  {o}
                </Tag>
              ))}
              {totalTags > 2 && (
                <Tag key={totalTags - 2} title={totalTags - 2}>
                  +{totalTags - 2}
                </Tag>
              )}
            </Tooltip>
          )
        }
        return []
      case CONSTANTS.FIELD_TYPES.DATE:
        if (typeof value == 'number') return moment.unix(value).format('ll')
        else if (value && typeof value == 'object') return value.format('ll')
        else return ''
      case CONSTANTS.FIELD_TYPES.TIME:
        return value
      case CONSTANTS.FIELD_TYPES.DATE_TIME:
        if (typeof value == 'number') return moment.unix(value).format('LLL')
        else if (value && typeof value == 'object') return value.format('LLL')
        else return ''
      case CONSTANTS.FIELD_TYPES.URL:
        return <>{value}</>
      case CONSTANTS.FIELD_TYPES.DATE_RANGE:
        if (value?.length === 2 && typeof value[0] == 'string')
          return (
            moment(value[0]).format('ll') +
            ' ~ ' +
            moment(value[1]).format('ll')
          )
        else if (value?.length === 2 && typeof value[0] == 'number')
          return (
            moment.unix(value[0]).format('ll') +
            ' ~ ' +
            moment.unix(value[1]).format('ll')
          )
        return ''
      case CONSTANTS.FIELD_TYPES.YES_NO:
        return value ? 'Yes' : 'No'
      default:
        return value
    }
  }

  const renderFileType = () => {
    const handleUploadAndSave = async (fileListFromModal) => {
        if (!fileListFromModal || fileListFromModal.length === 0) {
            onChange([], column, rowData);
            return;
        }
        try {
            // setLoading(true);
            const identityId = (await Auth.currentCredentials()).identityId;
            const uploadPromises = fileListFromModal.map(fileItem => {
                if (fileItem.originFileObj) {
                    const fileToUpload = fileItem.originFileObj;
                    return s3Upload(
                        fileToUpload,
                        identityId,
                        fileToUpload.name
                    );
                } else if (fileItem.url) {
                    return Promise.resolve(fileItem.url);
                }
                return Promise.resolve(null);
            });

            // Wait for ALL the uploads to complete.
            const finalUrlArray = await Promise.all(uploadPromises);

            // Filter out any null values from invalid items.
            const cleanUrlArray = finalUrlArray.filter(url => url !== null);
            
            // This is the key: Call the main `onChange` prop that was passed down to EditableCell.
            // This is how the table's main state gets updated.
            // `onChange` likely expects (value, column, rowData).
            onChange(cleanUrlArray, column, rowData);
            if (onDataChange) {
                onDataChange();
            }
            if (rowData.isGuest) {
                onDataChange();
            }

        } catch (err) {
            console.error("File upload failed in EditableCell:", err);
            message.error("File upload failed. Please try again.");
        } finally {
            // setLoading(false);
        }
    };
    const downloadFile = (url) => {
      // console.log('downloadFile', url);
      if (!url|| typeof url !== 'string') return;
      window.open(url, '_blank');
    };
    // console.log('FILE UPLOAD value:', value);
    const fileUrls = Array.isArray(value) ? value : (value ? [value] : []);
    // console.log('fileUrls:', fileUrls);
    return (
      <>
        {/* <Space> */}
          {fileUrls.map((url, index) => {
              if (!url || typeof url !== 'string') return null;
              let filename = url.split('/').pop();
              const separatorIndex = filename.indexOf('-');
              if (separatorIndex !== -1) {
                  filename = filename.substring(separatorIndex + 1);
              }
              return (
                <a 
                  key={index}
                  title={filename}
                  style={{ cursor: 'pointer' }}
                  onClick={() => downloadFile(url)}
                >
                  <FileOutlined 
                    key={index} 
                    title={filename} 
                    style={{ cursor: 'pointer'}}
                  />
                </a>
              );
          })}
        {/* </Space> */}
        {/* {console.log('fileUrls:', fileUrls)} */}
          <DirectUploadButton
              buttonText={<UploadOutlined />}
              maxCount={5}
              ogFileList={fileUrls}
              // The onSave prop is wired to our handler that will do the S3 upload.
              onSave={handleUploadAndSave}
          />
      </>
    )
  }

  return (
    <CellContainer
      onClick={handleClick}
      className={editing ? 'editing-cell' : ''}
    >
      {column?.type === CONSTANTS.FIELD_TYPES.FILE_UPLOAD ? (
        renderFileType()
      ) : (
        <>
          <div
            className={column?.catagory == 'Questions' ? 'question-cell' : ''}
          >
            {!editing && renderValue(cellData)}
          </div>
          {editing && renderInput()}
        </>
      )}
    </CellContainer>
  )
}

const ResizeableTitle = (props) => {
  const {
    onResize,
    onResizeStop,
    onResizeStart,
    width = 75,
    minWidth = 50,
    onClick = () => {},
    ...restProps
  } = props
  
  if (!width) {
    return <th {...restProps} />
  }

  return (
    <Resizable
      width={width}
      height={0}
      minConstraints={[minWidth, 0]}
      maxConstraints={[width * 3, 0]}
      onResize={onResize}
      onResizeStart={onResizeStart}
      onResizeStop={onResizeStop}
      onClick={(e) => {
        if (
          !e.target.className?.includes('react-resizable-handle') &&
          !e.target.className?.includes('react-resizable')
        )
          onClick(e)
      }}
    >
      <th key="a" {...restProps} />
    </Resizable>
  )
}
let uniqueId = 0
const getUniqueId = () => uniqueId++

export const ResizableTable = ({
  setColumns,
  columns,
  onResizeChange,
  fixedColumns = [], // Додаємо підтримку фіксованих колонок
  ...props
}) => {
  const latestColumnsRef = useRef(columns);
  const table = useRef(getUniqueId())
  const tableContainer = useRef(getUniqueId())
  const [w] = useWindowSize()
  const [tableWidth, setTableWidth] = useState(
    table?.current?.clientWidth || '100%',
  )
  const components = {
    header: {
      cell: ResizeableTitle,
    },
  }
  useEffect(() => {
    if (columns && tableContainer?.current?.clientWidth > 0) {
      let totalSize = 0
      columns.forEach((col) => {
        totalSize += col.width || 100
      })
      
      // Only set a fixed width if the total column width exceeds the container width
      if (totalSize > tableContainer.current.clientWidth) {
        setTableWidth(totalSize)
      } else {
        // If columns fit within container, use 100% to prevent unnecessary scrollbar
        setTableWidth('100%')
      }
    }
  }, [columns, w])

  const handleResize = (index) =>(e, { size }) => {
      const currentColumn = columns[index]
      const minWidth = currentColumn?.minWidth || 50
      
      if (size.width >= minWidth) {
        const nextColumns = [...columns]
        nextColumns[index] = {
          ...nextColumns[index],
          width: size.width,
        }
        latestColumnsRef.current = nextColumns; // Store the fresh data in the ref
        setColumns(nextColumns)
      }
    }

  const onResizeStart = () => {
  }

  const onResizeStop = () => {
    if (onResizeChange) {
      onResizeChange(latestColumnsRef.current);
    }
  }
  const columnList = columns?.map((col, index) => {
    // Визначаємо фіксацію колонки
    let fixed = undefined
    if (fixedColumns.includes(col.key) || col.key === 'action') {
      fixed = 'left' // Action колонка також фіксується зліва
    }
    
    return {
      ...col,
      fixed: fixed,
      onHeaderCell: (column) => {
        return {
          width: col.width || 100,
          minWidth: col.minWidth || 50,
          onResize: handleResize(index),
          onResizeStart: onResizeStart,
          onResizeStop: onResizeStop,
        }
      },
    }
  })
  return (
    <div
      ref={tableContainer}
      style={{ width: '100%', overflow: tableWidth === '100%' ? 'hidden' : 'auto' }}
      key={`rtable-${props.key || '1'}`}
    >
      <div style={{ width: tableWidth }}>
        <Table
          locale={{ emptyText: 'There is no data available.' }}
          tableLayout="fixed"
          columns={columnList}
          components={components}
          ref={table}
          showSorterTooltip={false}
          {...props}
        />
      </div>
    </div>
  )
}
